import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useSelector } from 'react-redux';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Paper,
  Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography
} from '@mui/material';
import { AddRounded, DeleteRounded, DownloadRounded, EditRounded, PersonPinRounded, UploadFileRounded } from '@mui/icons-material';
import Navbar from './Navbar';
import '../../services/GlobalVariables';

const emptyForm = { id: null, name: '', status: 'Active' };
const getApiError = (err, fallback) => {
  const serverMessage = err.response?.data?.message;
  const serverDetail = err.response?.data?.error;
  return serverMessage && serverDetail ? `${serverMessage}: ${serverDetail}` : serverMessage || err.message || fallback;
};

export default function OrderTakerManagement() {
  const { user } = useSelector((state) => state.auth);
  const excelInputRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadOrderTakers = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const response = await axios.get(`${global.SET_API_URL}posBusiness/orderTakers`);
      setRows(response.data.orderTakers || []);
    } catch (err) { setError(getApiError(err, 'Order Takers could not be loaded.')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOrderTakers(); }, [loadOrderTakers]);
  const filteredRows = useMemo(() => rows.filter((row) => `${row.name} ${row.status}`.toLowerCase().includes(search.trim().toLowerCase())), [rows, search]);

  const showDialog = (row = null) => {
    setForm(row ? { id: row.id, name: row.name, status: row.status } : emptyForm);
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return setError('Order Taker name is required.');
    setLoading(true); setError('');
    try {
      const response = await axios.post(`${global.SET_API_URL}posBusiness/orderTakers`, {
        ...form, cmpID: user?.cmpID || 1, branchID: user?.defaultBranchID || user?.branchID || 1
      });
      setMessage(response.data.message); setOpen(false); setForm(emptyForm); await loadOrderTakers();
    } catch (err) { setError(getApiError(err, 'Order Taker could not be saved.')); setLoading(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete Order Taker "${row.name}"?`)) return;
    setLoading(true); setError('');
    try {
      const response = await axios.delete(`${global.SET_API_URL}posBusiness/orderTakers/${row.id}`);
      setMessage(response.data.message); await loadOrderTakers();
    } catch (err) { setError(getApiError(err, 'Order Taker could not be deleted.')); setLoading(false); }
  };

  const downloadTemplate = () => {
    const worksheet = XLSX.utils.json_to_sheet([{ OrderTakerName: 'Muhammad Ali', Status: 'Active' }]);
    worksheet['!cols'] = [{ wch: 30 }, { wch: 15 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Order Takers');
    XLSX.writeFile(workbook, 'order_takers_import_template.xlsx');
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true); setError(''); setMessage('');
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const orderTakers = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
      if (!orderTakers.length) throw new Error('Excel file mein koi Order Taker record nahi hai');
      const response = await axios.post(`${global.SET_API_URL}posBusiness/orderTakers/import`, {
        orderTakers, cmpID: user?.cmpID || 1, branchID: user?.defaultBranchID || user?.branchID || 1
      });
      setMessage(response.data.message); await loadOrderTakers();
    } catch (err) { setError(getApiError(err, 'Order Takers could not be imported.')); }
    finally { setImporting(false); event.target.value = ''; }
  };

  return <>
    <Navbar />
    <Box sx={{ minHeight: 'calc(100vh - 64px)', bgcolor: 'var(--web-theme-page-bg, #F5F7FB)', py: { xs: 2, md: 4 } }}>
      <Container maxWidth="xl">
        <Paper elevation={0} sx={{ mb: 3, p: { xs: 2.25, md: 3 }, borderRadius: 'var(--web-theme-radius, 12px)', color: 'var(--web-theme-topbar-text, #fff)', background: 'linear-gradient(135deg, var(--web-theme-topbar, #780202), var(--web-theme-primary, #A80707))' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2}>
            <Stack direction="row" spacing={2} alignItems="center"><Box sx={{ width: 50, height: 50, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: 'rgba(255,255,255,.15)' }}><PersonPinRounded /></Box><Box><Typography variant="h4" fontWeight={800}>Order Takers</Typography><Typography sx={{ opacity: .82 }}>Create and manage POS order-taking staff from one place</Typography></Box></Stack>
            <Chip label={`${rows.length} records`} sx={{ bgcolor: 'rgba(255,255,255,.16)', color: 'inherit', fontWeight: 700, border: '1px solid rgba(255,255,255,.22)' }} />
          </Stack>
        </Paper>

        {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Paper variant="outlined" sx={{ mb: 3, p: 2, borderRadius: 'var(--web-theme-radius, 12px)', borderColor: 'var(--web-theme-primary, #780202)', bgcolor: 'var(--web-theme-card-bg, #fff)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
            <Box><Typography fontWeight={750}>Bulk Excel tools</Typography><Typography variant="body2" color="text.secondary">Download the template, fill names and status, then import the file.</Typography></Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}><Button variant="outlined" startIcon={<DownloadRounded />} onClick={downloadTemplate}>Download Template</Button><Button variant="contained" startIcon={<UploadFileRounded />} disabled={importing} onClick={() => excelInputRef.current.click()}>{importing ? 'Importing...' : 'Import Excel'}</Button><input ref={excelInputRef} hidden type="file" accept=".xlsx,.xls" onChange={handleImport} /></Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} variant="outlined" sx={{ borderRadius: 'var(--web-theme-radius, 12px)', overflow: 'hidden', bgcolor: 'var(--web-theme-card-bg, #fff)' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} sx={{ p: 2 }}>
            <TextField size="small" label="Search records" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} sx={{ maxWidth: 380, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Button variant="contained" startIcon={<AddRounded />} onClick={() => showDialog()}>Add Order Taker</Button>
          </Stack>
          <TableContainer sx={{ borderTop: '1px solid rgba(15,23,42,.08)' }}><Table size="small">
            <TableHead><TableRow>{['Order Taker Name', 'Status', 'Actions'].map((title, index) => <TableCell key={title} align={index === 2 ? 'right' : 'left'} sx={{ bgcolor: 'var(--web-theme-primary, #2563eb)', color: 'var(--web-theme-button-text, #fff)', fontWeight: 800, py: 1.6 }}>{title}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {loading && !rows.length ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5 }}><CircularProgress size={28} /></TableCell></TableRow> : !filteredRows.length ? <TableRow><TableCell colSpan={3} align="center" sx={{ py: 5, color: 'text.secondary' }}>No Order Takers found.</TableCell></TableRow> : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => <TableRow key={row.id} sx={{ bgcolor: index % 2 ? 'rgba(248,250,252,.7)' : '#fff', '&:hover': { bgcolor: 'rgba(37,99,235,.06)' } }}><TableCell sx={{ py: 1.5, borderColor: 'rgba(15,23,42,.08)' }}>{row.name}</TableCell><TableCell sx={{ borderColor: 'rgba(15,23,42,.08)' }}><Chip size="small" label={row.status} color={row.status === 'Active' ? 'success' : 'default'} variant="outlined" /></TableCell><TableCell align="right" sx={{ borderColor: 'rgba(15,23,42,.08)' }}><Tooltip title="Edit"><IconButton color="primary" size="small" onClick={() => showDialog(row)}><EditRounded /></IconButton></Tooltip><Tooltip title="Delete"><IconButton color="error" size="small" onClick={() => remove(row)}><DeleteRounded /></IconButton></Tooltip></TableCell></TableRow>)}
            </TableBody>
          </Table></TableContainer>
          <TablePagination component="div" count={filteredRows.length} page={page} rowsPerPage={rowsPerPage} rowsPerPageOptions={[5, 10, 25, 50, 100]} onPageChange={(_, value) => setPage(value)} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }} sx={{ bgcolor: 'rgba(248,250,252,.82)', borderTop: '1px solid rgba(15,23,42,.08)' }} />
        </Paper>
      </Container>
    </Box>

    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}><DialogTitle fontWeight={800}>{form.id ? 'Edit Order Taker' : 'Add Order Taker'}</DialogTitle><DialogContent sx={{ pt: '12px !important' }}><TextField autoFocus fullWidth label="Order Taker Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} inputProps={{ maxLength: 50 }} sx={{ mb: 2 }} /><FormControl fullWidth><InputLabel>Status</InputLabel><Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}><MenuItem value="Active">Active</MenuItem><MenuItem value="Inactive">Inactive</MenuItem></Select></FormControl></DialogContent><DialogActions sx={{ p: 2 }}><Button onClick={() => setOpen(false)}>Cancel</Button><Button variant="contained" onClick={save} disabled={loading}>{loading ? 'Saving...' : 'Save'}</Button></DialogActions></Dialog>
  </>;
}